import * as React from 'react';
import { withStyles, createStyles, StyledComponentProps } from '@material-ui/core/styles';
import { Table, TableBody, TableCell, TableRow } from '@material-ui/core';
import InlineActionButton from '@markinson/uicomponents-v2/InlineActionButton';
import { HotKeys } from 'react-hotkeys';
import { TAB, HotKeyNames } from 'utils/constants';
import FilterDialog from 'components/common/FilterDialog/FilterDialog.container';

const styles = createStyles({
  table: {
    marginBottom: '8px'
  },
  filterCell: {
    width: '170px',
    backgroundColor: '#eeeeee',
    border: '1px solid #fff',
    padding: '10px',

  },
  filterLabel: {
    fontSize: '14px',
    fontWeight: 'bold',
    textTransform: 'uppercase',
    lineHeight: '34px'
  },
  valueCell: {
    backgroundColor: '#dddddd',
    border: '1px solid #fff',
    padding: '10px',
    fontSize: '15px',
    color: 'rgba(0, 0, 0, 0.87)',
  },
  filterMenuPaper: {
    boxSizing: 'border-box',
    position: 'absolute',
    width: '400px',
    overflowWrap: 'anywhere',
    height: 'auto',
    zIndex: 100,
  },
  footer: {
    backgroundColor: '#e0e0e0',
    height: '50px'
  },
  applyButton: {
    flexBasis: '50%',
    boxShadow: 'none',
    borderRadius: 0,
    float: 'right',
    lineHeight: '34px',
    padding: '8px 16px'
  },
  fieldsContainer: {
    padding: '15px'
  }
});

const inlineStyles = createStyles({
  FilterButton: {
    backgroundColor: '#eeeeee',
    float: 'right'
  }
});

export interface IFilterRowProperties extends StyledComponentProps {
  formName: string;
  form: string;
  isValid: boolean;
  values: any;
  hidePreFilters: boolean;
  disableInitialApplyCall?: boolean;
  initialValues: any;
  parameters: [IParameters];
  reqParams: any;
  valuesSchema: any;
  lookupList: any;
  operationMode: string;
  rowModelType?: string;
  filterButtonRef?: React.MutableRefObject<HTMLButtonElement>;
  filterRef?: any;
  onBlur(event: any): boolean;
  reset(formName: string): void;
  fetchLookups(something: any): any;
  onFilterButtonShiftTab?(): any;
  onFilterButtonTab?(): any;
  onApplyFilters?(filters: any): void;
}

interface IFilterRowState {
  filterDropDownOpen: boolean;
  readOnly: boolean;
  apply: boolean;
  appliedFilters: string;
  lookups: {
    data: { [x: string]: any };
    loading: { [x: string]: boolean };
    hasError: { [x: string]: boolean };
    isFocused: { [x: string]: boolean };
  };
}

interface IParameters {
  id: number;
  type: string;
  props: object;
  [x: string]: any;
}

const DELAY = 10;

class FilterRow extends React.Component<IFilterRowProperties, IFilterRowState> {

  handler: any = null;
  constructor(props: Readonly<IFilterRowProperties>) {
    super(props);

    this.state = {
      filterDropDownOpen: false,
      apply: false,
      appliedFilters: '',
      readOnly: false,
      lookups: { data: {}, loading: {}, hasError: {}, isFocused: {} }
    };

    this.onEnter = this.onEnter.bind(this);
    this.onEscape = this.onEscape.bind(this);
    this.filterBtnKeyDown = this.filterBtnKeyDown.bind(this);
    this.handler = {
      [HotKeyNames.Enter]: this.onEnter,
      [HotKeyNames.Escape]: this.onEscape,
    };
  }

  onEnter(): void {
    if (this.state.filterDropDownOpen) {
      this.applyFilter();
    }
  }
  onEscape(): void {
    const { formName, reset } = this.props;
    if (this.state.filterDropDownOpen) {
      if (reset) {
        reset(formName);
      }
      this.setState({ filterDropDownOpen: false });
      setTimeout(() => this.props.filterButtonRef?.current?.focus(), DELAY);
      if (this.props?.filterRef) {
        this.props.filterRef.current.isOpen = false;
      }
    }
  }

  componentDidMount(): void {
    const { onApplyFilters, disableInitialApplyCall = false, rowModelType, form, values, reqParams } = this.props;
    this.initialFilters();
    if (!disableInitialApplyCall && onApplyFilters) {
      if (rowModelType === 'serverSide') {
        onApplyFilters({ formName: form, filters: values });
      } else {
        onApplyFilters({ ...reqParams, filters: values });
      }
    }
  }

  handleFilterClick = () => {
    this.setState({ filterDropDownOpen: !this.state.filterDropDownOpen });
    if (this.props?.filterRef) {
      this.props.filterRef.current.isOpen = !this.state.filterDropDownOpen;
    }
  }

  applyFilter = () => {
    this.setState({ apply: true });
  }

  stringGenerator = (field: any, displayValue: any) => {
    const { valuesSchema } = this.props;
    const trimmedValue = typeof displayValue === 'string' ? displayValue.trim() : displayValue;
    if (!field || !trimmedValue) return '';

    const isAutocomplete = field.type === 'AUTOCOMPLETE_FIELD';
    const isSelect = field.type === 'SELECT_FIELD';
    const isExLookup = field.type === 'EX_LOOKUP_FIELD';
    const label = field.props?.label || '';

    // Handle Autocomplete field options
    if (isAutocomplete) {
      const option = field.props?.options?.find((fieldOption) => fieldOption.value === displayValue);
      if (option || (this.props?.lookupList && this.props?.lookupList[field.props?.apiPrefs?.path] && this.getLookupValue(field, displayValue))) {
        return this.generateSpan(label, option?.label || this.getLookupValue(field, displayValue));
      }

      return '';
    }

    // Handle other fields (Select, Lookup, etc.)
    return this.generateSpan(label, this.getValueForDisplay(field, displayValue, isSelect, isExLookup, valuesSchema));
  };

  getValueForDisplay = (field: any, displayValue: any, isSelect: boolean, isExLookup: boolean, valuesSchema: any) => {
    if (isSelect) {
      return field.props?.options?.find((option) => option.value === displayValue)?.label || displayValue;
    }
    if (isExLookup) {
      return this.state.lookups.data[field.props.name]?.Display || valuesSchema?.[field.props.name]?.Display || displayValue;
    }

    return displayValue;
  };

  generateSpan = (label: string, value: string) => `<span style="font-size:12px; text-transform:uppercase;">${label}: </span><span style="font-weight:bold;">${value}</span>`;

  getLookupValue = (field: any, displayValue: string) => {
    const { lookupList } = this.props;
    if (lookupList && lookupList[field.props.apiPrefs.path]) {
      return this.getLabel(field, displayValue);
    } else {
      this.props.fetchLookups(field.props.apiPrefs);
    }

    return '-';
  }

  getLabel = (field: any, displayValue: string) => {
    const { lookupList } = this.props;
    const lookup = lookupList[field.props.apiPrefs.path];
    const label = lookup.Data.find((option) => option.value === displayValue) ? lookup.Data.find((option) => option.value === displayValue).label : '';

    return label;
  }

  handleApply = () => {
    const { isValid, values, reqParams, onApplyFilters, rowModelType, form } = this.props;
    if (isValid) {
      this.updateFilterString();
      if (onApplyFilters) {
        if (rowModelType === 'serverSide') {
          onApplyFilters({ formName: form, filters: { ...values, DefaultSearch: !this.state.apply } });

        } else {
          onApplyFilters({ ...reqParams, filters: { ...values, DefaultSearch: !this.state.apply } });
        }
      }
    }
    this.setState({ apply: false });
  }

  initialFilters = () => {
    const {
      hidePreFilters
    } = this.props;
    if (!hidePreFilters) {
      this.updateFilterString();
    } else {
      this.setState({ appliedFilters: '' });
    }

  }

  updateFilterString = () => {
    const { values, parameters } = this.props;
    const appliedFilters = [];

    parameters.forEach((param: any) => {
      const currentFilterValue = (values && values[param.props.name]) ?? '';
      if (currentFilterValue !== '' && currentFilterValue !== null) {
        let displayValue = currentFilterValue;
        if (currentFilterValue === false) { displayValue = 'No'; }
        if (currentFilterValue === true) { displayValue = 'Yes'; }
        appliedFilters.push(this.stringGenerator(param, displayValue));
      }
    });

    this.setState({
      filterDropDownOpen: false,
      appliedFilters: appliedFilters.filter(Boolean).join(' | ')
    });
  }

  filterBtnKeyDown(e: React.KeyboardEvent<HTMLSpanElement>): void {
    const { onFilterButtonShiftTab, onFilterButtonTab } = this.props;

    if (e?.keyCode === TAB && e?.shiftKey) {
      if (onFilterButtonShiftTab) {
        onFilterButtonShiftTab();
      }
      e.preventDefault();
    }

    if (e?.keyCode === TAB && !e?.shiftKey) {
      if (onFilterButtonTab) {
        onFilterButtonTab();
      }
      e.preventDefault();
    }
  }

  render(): React.ReactNode {
    const { classes, parameters, reqParams, filterButtonRef, formName, initialValues, values } = this.props;

    return (
      <HotKeys handlers={this.handler}>
        <Table className={classes.table}>
          <TableBody>
            <TableRow>
              <TableCell className={classes.filterCell}>
                <span className={classes.filterLabel}>Filters</span>
                <span onKeyDown={this.filterBtnKeyDown}>
                  <InlineActionButton
                    buttonRef={filterButtonRef}
                    iconName={'Filter'}
                    onClick={this.handleFilterClick}
                    style={inlineStyles.FilterButton}
                  />
                </span>
                {this.state.filterDropDownOpen ?
                  <FilterDialog
                    disableInitialApplyCall={true}
                    parameters={parameters}
                    formName={formName}
                    onApplyFilters={this.handleApply}
                    initialValues={values || initialValues}
                    onFilterButtonShiftTab={() => { return; }}
                    handleClose={this.onEscape}
                    reqParams={reqParams}
                  />
                  : null}
              </TableCell>
              <TableCell className={classes.valueCell} dangerouslySetInnerHTML={{ __html: this.state.appliedFilters }}></TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </HotKeys>
    );
  }
}

export default withStyles(styles, { index: 1 })(FilterRow);
